import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { ShoppingEntry } from '../../models/ShoppingEntry';
import { addIngredientsToShoppingList, deleteShoppingListEntry, getAllShoppingEntries, setEntryChecked, subscribeToShoppingEvents } from '../../utils/ShoppingService';
import { AddEntry, AddItemComposer } from './AddItemComposer';
import { PullToRefresh } from '../general/PullToRefresh';

const AISLE_ORDER = ['Unsorted', 'Vegetables', 'Snacks', 'Bakery', 'Frozen', 'Canned', 'Drinks'];

const AISLE_I18N_KEY: Record<string, string> = {
    Unsorted: 'sl_unsorted',
    Vegetables: 'sl_vegetables',
    Snacks: 'sl_snacks',
    Bakery: 'sl_bakery',
    Frozen: 'sl_frozen',
    Canned: 'sl_canned',
    Drinks: 'sl_drinks',
};

const AISLE_COLORS: Record<string, string> = {
    Unsorted: '#A99E8B',
    Vegetables: '#5C9A5C',
    Snacks: '#C2703F',
    Bakery: '#C99A53',
    Frozen: '#6E93B0',
    Canned: '#9C8AA6',
    Drinks: '#4E9AA0',
};

type LocalEntry = ShoppingEntry & { justAdded?: boolean };

// ── Swipeable row ─────────────────────────────────────────────────────────────

type SwipeableRowProps = {
    onDelete: () => void;
    children: React.ReactNode;
    rowStyle?: React.CSSProperties;
};

function SwipeableRow({ onDelete, children, rowStyle }: SwipeableRowProps) {
    const rowRef = useRef<HTMLDivElement>(null);
    const startXRef = useRef<number | null>(null);
    const currentOffsetRef = useRef(0);

    const applyTransform = (dx: number) => {
        if (rowRef.current) rowRef.current.style.transform = `translateX(${dx}px)`;
    };

    const handleStart = (x: number) => { startXRef.current = x; };

    const handleMove = (x: number) => {
        if (startXRef.current === null) return;
        const delta = Math.min(0, x - startXRef.current);
        currentOffsetRef.current = delta;
        applyTransform(delta);
    };

    const handleEnd = () => {
        if (startXRef.current === null) return;
        startXRef.current = null;
        if (currentOffsetRef.current < -80) {
            onDelete();
        } else {
            currentOffsetRef.current = 0;
            if (rowRef.current) {
                rowRef.current.style.transition = 'transform 0.2s ease';
                applyTransform(0);
                setTimeout(() => { if (rowRef.current) rowRef.current.style.transition = ''; }, 220);
            }
        }
    };

    return (
        <div style={{ position: 'relative', marginBottom: 7, borderRadius: 15, overflow: 'hidden' }}>
            {/* Delete reveal layer */}
            <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'flex-end', gap: 7, background: '#C16544', borderRadius: 15, paddingRight: 24,
            }}>
                <span style={{ font: "700 18px 'Inter', sans-serif", color: '#fff' }}>⌫</span>
                <span style={{ font: "600 13px 'Inter', sans-serif", color: '#fff' }}>Delete</span>
            </div>
            {/* Sliding row */}
            <div
                ref={rowRef}
                style={{ position: 'relative', ...rowStyle }}
                onTouchStart={e => handleStart(e.touches[0].clientX)}
                onTouchMove={e => { e.preventDefault(); handleMove(e.touches[0].clientX); }}
                onTouchEnd={handleEnd}
                onMouseDown={e => handleStart(e.clientX)}
                onMouseMove={e => { if (startXRef.current !== null) handleMove(e.clientX); }}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
            >
                {children}
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ShoppingList() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    const [entries, setEntries] = useState<LocalEntry[]>([]);
    const [checked, setChecked] = useState<Set<number>>(new Set());
    const [composerOpen, setComposerOpen] = useState(false);
    const [undoItems, setUndoItems] = useState<ShoppingEntry[] | null>(null);
    const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Derived colors from theme ──────────────────────────────────────────────
    const bg = theme.palette.background.default;
    const cardBg = theme.palette.background.paper;
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const divider = theme.palette.divider;
    const primary = theme.palette.primary.main;         // amber
    const primaryDark = theme.palette.primary.dark;     // dark amber
    const primaryLight = isLight ? '#FCD34D' : '#FCD34D';
    const mutedText = isLight ? '#9A9182' : '#7A6A52';
    const cardBorder = isLight ? '#EDE0CC' : '#3D2A15';
    const justAddedBg = isLight ? 'rgba(217,119,6,0.06)' : 'rgba(245,158,11,0.08)';
    const justAddedBorder = primary;

    // ── Data ──────────────────────────────────────────────────────────────────
    const loadEntries = useCallback(() => {
        return getAllShoppingEntries().then((data: ShoppingEntry[]) => {
            setEntries(data);
            setChecked(new Set(data.filter(e => e.checked && e.id !== null).map(e => e.id!)));
        });
    }, []);

    useEffect(() => { loadEntries(); }, [loadEntries]);

    useEffect(() => {
        return subscribeToShoppingEvents((updated) => {
            if (updated.id === null) return;
            setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
            setChecked(prev => {
                const next = new Set(prev);
                if (updated.checked) next.add(updated.id!); else next.delete(updated.id!);
                return next;
            });
        });
    }, []);

    const grouped = AISLE_ORDER.map(aisle => ({
        aisle,
        items: entries.filter(e => (e.aisle || 'Unsorted') === aisle),
    })).filter(g => g.items.length > 0);

    const total = entries.length;
    const checkedCount = checked.size;
    const pct = total === 0 ? 0 : Math.round((checkedCount / total) * 100);
    const recentNames = Array.from(new Set(entries.map(e => e.name)));

    // ── Handlers ──────────────────────────────────────────────────────────────
    const toggleChecked = (id: number) => {
        const newState = !checked.has(id);
        setChecked(prev => {
            const next = new Set(prev);
            if (newState) next.add(id); else next.delete(id);
            return next;
        });
        setEntryChecked(id, newState).catch(() => {
            // Revert optimistic update on failure
            setChecked(prev => {
                const revert = new Set(prev);
                if (newState) revert.delete(id); else revert.add(id);
                return revert;
            });
        });
    };

    const handleAdd = useCallback(async (newEntry: AddEntry) => {
        const toAdd: ShoppingEntry = {
            id: null, name: newEntry.name,
            amount: newEntry.amount, unit: newEntry.unit,
            aisle: newEntry.aisle, username: '',
            checked: false,
        };
        try {
            const returned = await addIngredientsToShoppingList([toAdd]);
            if (returned.length > 0) {
                const added = returned[0];
                setEntries(prev => {
                    const idx = prev.findIndex(e => e.id === added.id);
                    if (idx >= 0) {
                        const next = [...prev];
                        next[idx] = { ...added, justAdded: true };
                        return next;
                    }
                    return [...prev, { ...added, justAdded: true }];
                });
                setTimeout(() => {
                    setEntries(prev => prev.map(e => e.id === added.id ? { ...e, justAdded: false } : e));
                }, 3000);
            }
        } catch (_) {}
    }, []);

    const handleDelete = useCallback(async (id: number) => {
        try { await deleteShoppingListEntry(id); } catch (_) {}
        setEntries(prev => prev.filter(e => e.id !== id));
        setChecked(prev => { const next = new Set(prev); next.delete(id); return next; });
    }, []);

    const handleClearAll = useCallback(async () => {
        const toDelete = entries.filter(e => e.id !== null && checked.has(e.id!));
        if (toDelete.length === 0) return;
        setUndoItems(toDelete);
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setEntries(prev => prev.filter(e => !checked.has(e.id!)));
        setChecked(new Set());
        await Promise.allSettled(toDelete.map(e => deleteShoppingListEntry(e.id!)));
        undoTimerRef.current = setTimeout(() => setUndoItems(null), 5000);
    }, [entries, checked]);

    const handleUndo = useCallback(async () => {
        if (!undoItems) return;
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoItems(null);
        try {
            const restored = await addIngredientsToShoppingList(undoItems);
            setEntries(prev => [...prev, ...restored]);
        } catch (_) {}
    }, [undoItems]);

    const fmtQty = (e: LocalEntry) => {
        if (e.amount && e.unit) return `${e.amount} ${e.unit}`;
        if (e.amount) return String(e.amount);
        if (e.unit) return e.unit;
        return '';
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div style={{
            display: 'flex', flexDirection: 'column',
            height: 'calc(100vh - 56px)',
            background: bg,
            fontFamily: "'Inter', sans-serif",
            position: 'relative', overflow: 'hidden',
        }}>
            {/* ── Header ── */}
            <div style={{ padding: '14px 22px 16px', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: textPrimary, letterSpacing: '-.02em' }}>
                        {t('shoppingList')}
                    </div>
                    <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
                        color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 17, fontWeight: 700,
                        boxShadow: `0 6px 14px -4px ${primary}80`,
                    }}>{total}</div>
                </div>

                {/* Progress bar */}
                <div style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 7 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: textSecondary }}>
                            {t('sl_inBasket', { checked: checkedCount, total })}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: primary }}>{pct}%</span>
                    </div>
                    <div style={{ height: 7, borderRadius: 6, background: divider, overflow: 'hidden' }}>
                        <div style={{
                            width: `${pct}%`, height: '100%',
                            background: `linear-gradient(90deg, ${primary}, ${primaryDark})`,
                            borderRadius: 6, transition: 'width .3s ease',
                        }} />
                    </div>
                </div>
            </div>

            {/* ── List ── */}
            <PullToRefresh
                onRefresh={loadEntries}
                style={{ flex: 1 }}
                contentStyle={{ padding: '2px 16px 4px' }}
            >
                {entries.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px 20px', fontSize: 15, color: mutedText }}>
                        {t('sl_empty')}
                    </div>
                )}
                {grouped.map(group => (
                    <div key={group.aisle}>
                        {/* Aisle header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 4px 6px' }}>
                            <span style={{
                                width: 8, height: 8, borderRadius: '50%',
                                background: AISLE_COLORS[group.aisle],
                                display: 'inline-block', flexShrink: 0,
                            }} />
                            <span style={{
                                fontSize: 11, fontWeight: 700, letterSpacing: '.12em',
                                textTransform: 'uppercase', color: mutedText,
                            }}>
                                {t(AISLE_I18N_KEY[group.aisle] ?? group.aisle, group.aisle)}
                            </span>
                        </div>

                        {group.items.map(item => {
                            const isChecked = item.id !== null && checked.has(item.id!);
                            const dotColor = AISLE_COLORS[item.aisle || 'Unsorted'] ?? primary;
                            const qty = fmtQty(item);
                            return (
                                <SwipeableRow
                                    key={item.id}
                                    onDelete={() => item.id !== null && handleDelete(item.id!)}
                                    rowStyle={{
                                        display: 'flex', alignItems: 'center', gap: 13,
                                        background: item.justAdded ? justAddedBg : cardBg,
                                        borderRadius: 15, padding: '12px 14px',
                                        boxShadow: isLight
                                            ? '0 1px 3px rgba(180,83,9,0.07)'
                                            : '0 1px 3px rgba(0,0,0,0.2)',
                                        border: `${item.justAdded ? 1.5 : 1}px solid ${item.justAdded ? justAddedBorder : cardBorder}`,
                                        cursor: 'pointer', userSelect: 'none',
                                    }}
                                >
                                    {/* Circle checkbox */}
                                    <div
                                        onClick={() => item.id !== null && toggleChecked(item.id!)}
                                        style={{
                                            flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 13, fontWeight: 700,
                                            background: isChecked ? primary : cardBg,
                                            border: `2px solid ${isChecked ? primary : dotColor}`,
                                            color: '#fff',
                                            transition: 'background .15s, border-color .15s',
                                        }}
                                    >
                                        {isChecked ? '✓' : ''}
                                    </div>

                                    {/* Name + just-added pill */}
                                    <div
                                        onClick={() => item.id !== null && toggleChecked(item.id!)}
                                        style={{ flex: 1, minWidth: 0 }}
                                    >
                                        <div style={{
                                            fontSize: 15, fontWeight: 600,
                                            color: isChecked ? mutedText : textPrimary,
                                            textDecoration: isChecked ? 'line-through' : 'none',
                                            textDecorationColor: mutedText,
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                            {item.name}
                                        </div>
                                        {item.justAdded && (
                                            <div style={{ display: 'inline-flex', alignItems: 'center', marginTop: 4 }}>
                                                <span style={{
                                                    fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
                                                    textTransform: 'uppercase',
                                                    color: primaryDark,
                                                    background: isLight ? 'rgba(217,119,6,0.12)' : 'rgba(245,158,11,0.15)',
                                                    padding: '2px 8px', borderRadius: 20,
                                                }}>{t('sl_justAdded')}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Quantity */}
                                    {qty && (
                                        <div style={{
                                            flexShrink: 0, fontSize: 13, fontWeight: 600,
                                            color: isChecked ? mutedText : primary,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}>
                                            {qty}
                                        </div>
                                    )}
                                </SwipeableRow>
                            );
                        })}
                    </div>
                ))}
            </PullToRefresh>

            {/* ── Bottom bar ── */}
            <div style={{
                flexShrink: 0, padding: '10px 18px 22px',
                background: isLight
                    ? `linear-gradient(to top, ${bg} 70%, transparent)`
                    : `linear-gradient(to top, ${bg} 70%, transparent)`,
            }}>
                {/* Clear checked */}
                {checkedCount > 0 && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14,
                        padding: '8px 8px 8px 14px', marginBottom: 9,
                        boxShadow: isLight
                            ? '0 3px 10px -5px rgba(180,83,9,0.15)'
                            : '0 3px 10px -5px rgba(0,0,0,0.3)',
                    }}>
                        <span style={{
                            width: 22, height: 22, borderRadius: '50%',
                            background: primary, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                        }}>✓</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: textSecondary }}>
                            {t('sl_itemsTicked', { count: checkedCount })}
                        </span>
                        <button
                            onClick={handleClearAll}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                fontSize: 13, fontWeight: 600, color: '#fff',
                                background: textPrimary,
                                padding: '8px 14px', borderRadius: 11,
                                border: 'none', cursor: 'pointer',
                            }}
                        >{t('sl_clearAll')}</button>
                    </div>
                )}

                {/* Add item button */}
                <div
                    onClick={() => setComposerOpen(true)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 11,
                        background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
                        borderRadius: 16, padding: '14px 18px',
                        boxShadow: `0 10px 22px -8px ${primary}90`,
                        cursor: 'pointer',
                    }}
                >
                    <span style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: 'rgba(255,255,255,.18)',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 20, lineHeight: 1, flexShrink: 0,
                    }}>+</span>
                    <span style={{ fontSize: 15, fontWeight: 500, color: 'rgba(255,255,255,.85)' }}>
                        {t('sl_addAnItem')}
                    </span>
                </div>
            </div>

            {/* ── Undo toast ── */}
            {undoItems && (
                <div style={{
                    position: 'absolute', left: 16, right: 16, bottom: 96,
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: isLight ? '#292524' : theme.palette.background.paper,
                    border: isLight ? 'none' : `1px solid ${divider}`,
                    borderRadius: 14, padding: '14px 16px',
                    boxShadow: '0 12px 30px -10px rgba(0,0,0,0.4)', zIndex: 100,
                }}>
                    <span style={{
                        width: 24, height: 24, borderRadius: '50%',
                        background: `${primary}30`, color: primaryLight,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                    }}>✓</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: isLight ? '#F4EFE3' : textPrimary }}>
                        {t('sl_itemsCleared', { count: undoItems.length })}
                    </span>
                    <button
                        onClick={handleUndo}
                        style={{
                            fontSize: 14, fontWeight: 700, color: primaryLight,
                            letterSpacing: '.02em', background: 'none', border: 'none', cursor: 'pointer',
                        }}
                    >{t('sl_undo')}</button>
                </div>
            )}

            {/* ── Add composer overlay ── */}
            <AddItemComposer
                open={composerOpen}
                onClose={() => setComposerOpen(false)}
                onAdd={handleAdd}
                recentNames={recentNames}
            />
        </div>
    );
}
